import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import BottomNav from '../components/BottomNav';

interface StockProduct {
  id: string;
  name: string;
  category_id: string | null;
  is_available: boolean;
  unit_price: number;
}

async function toggleProduct(productId: string, isAvailable: boolean) {
  try {
    const { error } = await supabase.functions.invoke('tapnpay-product-toggle-availability', {
      body: { product_id: productId, is_available: isAvailable },
    });
    if (error) throw error;
  } catch {
    await supabase
      .from('products')
      .update({ is_available: isAvailable })
      .eq('id', productId);
  }
}

export default function StockTogglesPage() {
  const { session } = useAuth();
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    if (!session?.storeId) return;
    setLoading(true);
    const { data } = await supabase
      .from('products')
      .select('id, name, category_id, is_available, unit_price, price')
      .eq('store_id', session.storeId)
      .order('name');
    const rows = (data ?? []).map((p) => {
      const pp = p as Record<string, unknown>;
      return {
        id: pp.id as string,
        name: (pp.name as string) ?? '',
        category_id: (pp.category_id as string | null) ?? null,
        is_available: pp.is_available !== false,
        unit_price: (pp.unit_price as number) ?? (pp.price as number) ?? 0,
      } satisfies StockProduct;
    });
    setProducts(rows);
    setLoading(false);
  }, [session?.storeId]);

  useEffect(() => {
    void fetchProducts();
    if (!session?.storeId) return;
    const channel = supabase
      .channel(`staff-stock-${session.storeId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products',
          filter: `store_id=eq.${session.storeId}`,
        },
        () => {
          void fetchProducts();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [session?.storeId, fetchProducts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name.toLowerCase().includes(q));
  }, [products, query]);

  const handleToggle = async (p: StockProduct) => {
    setBusy(p.id);
    const next = !p.is_available;
    // optimistic update
    setProducts((prev) =>
      prev.map((row) => (row.id === p.id ? { ...row, is_available: next } : row)),
    );
    try {
      await toggleProduct(p.id, next);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
          Disponibilité produits
        </p>
        <span className="text-[10px] text-on-surface-variant tabular-nums">
          {filtered.filter((p) => p.is_available).length}/{filtered.length}
        </span>
      </header>

      <div className="p-3" style={{ borderBottom: '0.5px solid #e5e7eb' }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher"
          className="w-full min-h-[44px] px-3 text-sm text-on-surface bg-surface"
          style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
        />
      </div>

      {loading ? (
        <div className="p-6 text-center text-sm text-on-surface-variant">…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-on-surface-variant">Aucun produit</div>
      ) : (
        <div className="bg-surface" style={{ borderTop: '0.5px solid #e5e7eb' }}>
          {filtered.map((p, i) => (
            <div
              key={p.id}
              className="flex items-center justify-between px-4 py-3 min-h-[56px]"
              style={{ borderBottom: i < filtered.length - 1 ? '0.5px solid #e5e7eb' : undefined }}
            >
              <div className="flex-1">
                <p className="text-sm text-on-surface">{p.name}</p>
                <p className="text-[11px] text-on-surface-variant tabular-nums">
                  {p.unit_price.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD
                </p>
              </div>
              <button
                type="button"
                disabled={busy === p.id}
                onClick={() => void handleToggle(p)}
                aria-pressed={p.is_available}
                className={`w-[60px] h-[32px] flex items-center transition-none ${
                  p.is_available ? 'bg-primary' : 'bg-surface-container-high'
                }`}
                style={{ border: '0.5px solid #000' }}
              >
                <span
                  className={`block w-[24px] h-[24px] mx-1 ${
                    p.is_available ? 'ml-auto bg-on-primary' : 'bg-on-surface-variant'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
